/**
 * <LiveReportCardSkeleton> — la forme d'une carte de rapport pendant un premier chargement.
 *
 * Une seule anatomie pour les deux cartes qui montrent un rapport, comme leur en-tête commun :
 *  - `size="md"` (défaut) — une carte de l'onglet Rapports (`<LiveReportCard>`), à sa largeur
 *    (`useLiveReportCardWidth`) ;
 *  - `size="sm"` — « Dernier bilan » sur l'accueil (`<LastReportCard>`), qui prend celle de sa
 *    colonne.
 *
 * Dedans : l'en-tête d'un rapport (`<LiveReportHeaderSkeleton>`, à la même taille), le commentaire
 * du tuteur sur `commentLines` lignes — celles de la carte qu'il remplace (deux dans la liste, trois
 * sur l'accueil : un commentaire de tuteur les remplit) —, et la place du chevron, tenue vide : il
 * ne charge rien.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { Card } from "../../molecules/Card"
import { LiveReportHeaderSkeleton } from "../../molecules/LiveReportHeader/LiveReportHeaderSkeleton"
import { CARD_MAX_WIDTH, useLiveReportCardWidth } from "./LiveReportCard"

export type LiveReportCardSkeletonProps = {
    size?: "sm" | "md"
    /** Les lignes du commentaire : le `numberOfLines` de la carte remplacée. */
    commentLines: 2 | 3
}

/** La dernière ligne d'un commentaire s'arrête avant le bord, comme un vrai texte. */
const COMMENT_WIDTHS = { 2: ["100%", "70%"], 3: ["100%", "90%", "50%"] } as const

export function LiveReportCardSkeleton(props: LiveReportCardSkeletonProps) {
    const { size = "md", commentLines } = props
    const width = useLiveReportCardWidth()

    return (
        <Card p="4" style={size === "md" ? styles.card(width) : undefined}>
            <Row gap="3" align="center">
                <Stack gap="3" style={styles.body}>
                    <LiveReportHeaderSkeleton size={size} />
                    <View>
                        {COMMENT_WIDTHS[commentLines].map((line, index) => (
                            <Skeleton key={index} textSize="text-lg" width={line} />
                        ))}
                    </View>
                </Stack>
                <View style={styles.chevron} />
            </Row>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    card: (width: number) => ({
        width,
        maxWidth: CARD_MAX_WIDTH,
        alignSelf: "center" as const,
    }),
    body: { flex: 1, flexShrink: 1 },
    /** La place du chevron des cartes (`<Icon size={20}>`). */
    chevron: { width: 20, height: 20 },
}))
